// pages/quotations/QuotationList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import Select from 'react-select';
import { DatePicker } from 'antd';
import Table from '../../core/pagination/datatable';
import { 
    ChevronUp, 
    RotateCcw, 
    Sliders, 
    StopCircle, 
    User, 
    Calendar,
    Download,
    Printer,
    Filter,
    Search,
    Eye,
    Edit,
    Trash2,
    PlusCircle,
    FileText,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Clock,
    RefreshCw,
    DollarSign,
    File
} from 'feather-icons-react/build/IconComponents';
import { quotationActions } from '../../core/action/quotationActions';
import { customerActions } from '../../core/action/customerActions';
import { companyActions } from '../../core/action/companyActions';
import ViewQuotation from '../../core/modals/quotations/ViewQuotation';
import EditQuotation from '../../core/modals/quotations/EditQuotation';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { RangePicker } = DatePicker;
const MySwal = withReactContent(Swal);

const QuotationList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [companySettings, setCompanySettings] = useState({});
    
    // Dropdown options
    const [customers, setCustomers] = useState([]);
    
    // Modal states
    const [modalType, setModalType] = useState(null); // 'view', 'edit'
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    
    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    
    // Filters
    const [filters, setFilters] = useState({
        search: '',
        startDate: null,
        endDate: null,
        customer_id: 0,
        status: ''
    });
    
    // Sort options
    const sortOptions = [
        { value: 'newest', label: 'Newest First' },
        { value: 'oldest', label: 'Oldest First' },
        { value: 'quotation_asc', label: 'Quotation No (A-Z)' },
        { value: 'quotation_desc', label: 'Quotation No (Z-A)' },
        { value: 'amount_high', label: 'Highest Amount' },
        { value: 'amount_low', label: 'Lowest Amount' },
        { value: 'customer_asc', label: 'Customer Name (A-Z)' },
        { value: 'customer_desc', label: 'Customer Name (Z-A)' }
    ];
    
    const [sortOption, setSortOption] = useState('newest');
    
    // Status filter options
    const statusFilterOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'expired', label: 'Expired' },
        { value: 'converted', label: 'Converted' }
    ];
    
    // Date range presets
    const datePresets = {
        'Today': [dayjs(), dayjs()],
        'Yesterday': [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')],
        'Last 7 Days': [dayjs().subtract(7, 'day'), dayjs()],
        'Last 30 Days': [dayjs().subtract(30, 'day'), dayjs()],
        'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
        'Last Month': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
    };

    // Load initial data
    useEffect(() => {
        loadCompanySettings();
        loadCustomers();
        loadQuotations();
    }, []);

    // Load company settings
    const loadCompanySettings = async () => {
        try {
            const result = await companyActions.getSettings();
            if (result.success) {
                setCompanySettings(result.data || {});
            }
        } catch (error) {
            console.error('Error loading company settings:', error);
        }
    };

    // Load customers
    const loadCustomers = async () => {
        try {
            const result = await customerActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: 0, label: 'All Customers' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    // Load quotations
    const loadQuotations = async (page = pagination.page) => {
        try {
            setLoading(true);
            
            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                start_date: filters.startDate ? filters.startDate.format('YYYY-MM-DD') : undefined,
                end_date: filters.endDate ? filters.endDate.format('YYYY-MM-DD') : undefined,
                customer_id: filters.customer_id || undefined,
                status: filters.status || undefined,
                sort: sortOption
            };
            
            const result = await quotationActions.getQuotations(params);
            
            if (result.success) {
                setQuotations(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load quotations',
                    timer: 2000
                });
            }
        } catch (error) {
            console.error('Error loading quotations:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load quotations',
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle search
    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadQuotations(1);
    };

    // Toggle filter visibility
    const toggleFilterVisibility = () => {
        setIsFilterVisible((prev) => !prev);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            search: '',
            startDate: null,
            endDate: null,
            customer_id: 0,
            status: ''
        });
        setSortOption('newest');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadQuotations(1);
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters(prev => ({
                ...prev,
                startDate: dates[0],
                endDate: dates[1]
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                startDate: null,
                endDate: null
            }));
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Handle sort change
    const handleSortChange = (option) => {
        setSortOption(option?.value || 'newest');
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => loadQuotations(1), 0);
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
        loadQuotations(page);
    };

    // Modal handlers
    const handleView = (quotation) => {
        setSelectedQuotation(quotation);
        setModalType('view');
    };

    const handleEdit = (quotation) => {
        setSelectedQuotation(quotation);
        setModalType('edit');
    };

    const handleModalClose = () => {
        setModalType(null);
        setSelectedQuotation(null);
    };

    const handleSuccess = () => {
        loadQuotations(pagination.page);
        handleModalClose();
    };

    // Handle delete
    const handleDelete = async (quotation) => {
        const result = await MySwal.fire({
            title: 'Delete Quotation?',
            html: `
                <div class="text-start">
                    <p>Are you sure you want to delete this quotation?</p>
                    <p><strong>Quotation No:</strong> ${quotation.quotation_no}</p>
                    <p><strong>Customer:</strong> ${quotation.customer_name}</p>
                    <p><strong>Amount:</strong> ₹${parseFloat(quotation.grand_total || 0).toFixed(2)}</p>
                    <p class="text-danger">This action cannot be undone.</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const deleteResult = await quotationActions.deleteQuotation(quotation.id);
                
                if (deleteResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Quotation has been deleted.',
                        timer: 2000
                    });
                    loadQuotations(pagination.page);
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: deleteResult.message || 'Failed to delete quotation',
                        timer: 2000
                    });
                }
            } catch (error) {
                console.error('Error deleting quotation:', error);
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete quotation',
                    timer: 2000
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle convert to sale
    const handleConvertToSale = async (id) => {
        const result = await MySwal.fire({
            title: 'Convert to Sale?',
            text: 'This will create a sales invoice from this quotation. Continue?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, convert'
        });

        if (result.isConfirmed) {
            try {
                const convertResult = await quotationActions.convertToSale(id);
                if (convertResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Converted!',
                        text: 'Quotation converted to sale successfully',
                        timer: 2000
                    });
                    loadQuotations(pagination.page);
                } else {
                    MySwal.fire('Error', convertResult.message || 'Failed to convert', 'error');
                }
            } catch (error) {
                console.error('Error converting quotation:', error);
                MySwal.fire('Error', 'Failed to convert quotation', 'error');
            }
        }
    };

// pages/quotations/QuotationList.jsx - FIXED Calculation Logic

/**
 * Professional PDF Generation Function - WITH CORRECT CALCULATIONS
 */
const downloadQuotationPDF = async (quotation) => {
    try {
        setLoading(true);
        
        // Show loading message
        MySwal.fire({
            title: 'Generating PDF...',
            text: 'Please wait',
            allowOutsideClick: false,
            didOpen: () => {
                MySwal.showLoading();
            }
        });

        // Fetch full quotation details including items
        const result = await quotationActions.getQuotation(quotation.id);
        
        if (!result.success || !result.data) {
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: result?.message || 'Failed to load quotation details',
                timer: 2000
            });
            return;
        }

        const fullQuotation = result.data;
        
        // Create new PDF document
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });
        
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 20;
        let yPos = margin;
        
        // =====================================================
        // HEADER SECTION - Professional Letterhead
        // =====================================================
        
        // Top decorative line
        doc.setDrawColor(66, 103, 178);
        doc.setFillColor(66, 103, 178);
        doc.rect(0, 0, pageWidth, 5, 'F');
        
        // Company Logo (if exists)
        if (companySettings?.company_logo) {
            try {
                let logoUrl = companySettings.company_logo;
                if (!logoUrl.startsWith('http')) {
                    logoUrl = logoUrl.replace(/^\/+/, '');
                    logoUrl = `${window.location.origin}/${logoUrl}`;
                }
                doc.addImage(logoUrl, 'PNG', margin, yPos - 5, 35, 12, undefined, 'FAST');
            } catch (logoError) {
                console.warn('Could not add logo:', logoError);
            }
        }
        
        // Company Name
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text(companySettings?.company_name || 'Your Company Name', margin + 40, yPos);
        
        // Company GST
        if (companySettings?.gst_number) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(`GSTIN: ${companySettings.gst_number}`, margin + 40, yPos + 7);
        }
        
        yPos += 15;
        
        // Company Address
        if (companySettings?.company_address) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(120, 120, 120);
            const addressLines = doc.splitTextToSize(companySettings.company_address, 80);
            doc.text(addressLines, margin, yPos);
        }
        
        // Company Contact Information (Right side)
        const contactX = pageWidth - margin - 60;
        let contactY = yPos - 10;
        
        if (companySettings?.company_phone) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(80, 80, 80);
            doc.text(`Ph: ${companySettings.company_phone}`, contactX, contactY);
            contactY += 4;
        }
        
        if (companySettings?.company_email) {
            doc.setFontSize(8);
            doc.text(`Email: ${companySettings.company_email}`, contactX, contactY);
            contactY += 4;
        }
        
        if (companySettings?.company_website) {
            doc.setFontSize(8);
            doc.text(`Web: ${companySettings.company_website}`, contactX, contactY);
        }
        
        yPos += 10;
        
        // Decorative separator
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
        
        // =====================================================
        // DOCUMENT TITLE
        // =====================================================
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(66, 103, 178);
        doc.text('QUOTATION', pageWidth / 2, yPos, { align: 'center' });
        
        yPos += 8;
        
        // Quotation Number and Date
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        
        const quotationNo = fullQuotation.quotation_no || quotation.quotation_no || 'N/A';
        const quotationDate = fullQuotation.quotation_date || quotation.quotation_date || null;
        
        doc.text(`Quotation #: ${quotationNo}`, margin, yPos);
        doc.text(`Date: ${quotationDate ? dayjs(quotationDate).format('DD MMM YYYY') : 'N/A'}`, pageWidth - margin - 50, yPos);
        
        yPos += 6;
        
        // Valid Till
        const expiryDate = fullQuotation.expiry_date || quotation.expiry_date;
        if (expiryDate) {
            doc.text(`Valid Until: ${dayjs(expiryDate).format('DD MMM YYYY')}`, margin, yPos);
            yPos += 6;
        }
        
        yPos += 5;
        
        // =====================================================
        // BILL TO / CUSTOMER INFORMATION
        // =====================================================
        
        // Section Title
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPos - 4, pageWidth - (margin * 2), 6, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('BILL TO', margin + 2, yPos);
        
        yPos += 8;
        
        // Customer Details
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        
        let customerY = yPos;
        
        // Customer Name with Phone - FIXED: Removed duplicate phone
        const customerName = fullQuotation.customer_name || quotation.customer_name || 'N/A';
        const customerPhone = fullQuotation.customer_phone || quotation.customer_phone;
        
        doc.setFont('helvetica', 'bold');
        doc.text('Name:', margin, customerY);
        doc.setFont('helvetica', 'normal');
        
        // Show phone only once
        const customerDisplay = customerPhone ? `${customerName} (${customerPhone})` : customerName;
        doc.text(customerDisplay, margin + 20, customerY);
        
        customerY += 6;
        
        // Customer Email
        const customerEmail = fullQuotation.customer_email || quotation.customer_email;
        if (customerEmail) {
            doc.setFont('helvetica', 'bold');
            doc.text('Email:', margin, customerY);
            doc.setFont('helvetica', 'normal');
            doc.text(customerEmail, margin + 20, customerY);
        }
        
        // Customer GST
        const customerGst = fullQuotation.customer_gst || quotation.customer_gst;
        if (customerGst) {
            // Position GST on the right side
            doc.setFont('helvetica', 'bold');
            doc.text('GSTIN:', margin + 100, customerY);
            doc.setFont('helvetica', 'normal');
            doc.text(customerGst, margin + 120, customerY);
        }
        
        customerY += 6;
        
        // Customer Address
        const customerAddress = fullQuotation.customer_address || quotation.customer_address;
        if (customerAddress) {
            doc.setFont('helvetica', 'bold');
            doc.text('Address:', margin, customerY);
            doc.setFont('helvetica', 'normal');
            const addressLine = doc.splitTextToSize(customerAddress, 150);
            doc.text(addressLine, margin + 25, customerY);
            customerY += (addressLine.length * 5);
        }
        
        yPos = customerY + 10;
        
        // =====================================================
        // ITEMS TABLE
        // =====================================================
        
        const tableBody = [];
        const items = fullQuotation.items || quotation.items || [];
        
        // Use values directly from the quotation data instead of recalculating
        const subtotal = parseFloat(fullQuotation.subtotal || quotation.subtotal || 0);
        const tax = parseFloat(fullQuotation.tax || quotation.tax || 0);
        const discount = parseFloat(fullQuotation.discount || quotation.discount || 0);
        const shipping = parseFloat(fullQuotation.shipping || quotation.shipping || 0);
        const adjustment = parseFloat(fullQuotation.adjustment || quotation.adjustment || 0);
        const roundOff = parseFloat(fullQuotation.round_off || quotation.round_off || 0);
        const grandTotal = parseFloat(fullQuotation.grand_total || quotation.grand_total || 0);
        
        if (items && items.length > 0) {
            items.forEach((item, index) => {
                const productName = item.product_name || item.productName || 'Product';
                const hsnCode = item.hsn_code || item.hsnCode || '-';
                const quantity = parseFloat(item.quantity) || 1;
                const unitPrice = parseFloat(item.unit_price || item.unitPrice || item.price || 0);
                const discountPercent = parseFloat(item.discount) || 0;
                const taxPercent = parseFloat(item.tax_percent || item.taxPercent || 0);
                const totalAmount = parseFloat(item.total_cost || item.totalCost || item.amount || 0);
                
                tableBody.push([
                    (index + 1).toString(),
                    productName,
                    hsnCode,
                    quantity.toString(),
                    unitPrice.toFixed(2),
                    `${discountPercent}%`,
                    `${taxPercent}%`,
                    totalAmount.toFixed(2)
                ]);
            });
        } else {
            tableBody.push(['1', 'No items', '-', '0', '0.00', '0%', '0%', '0.00']);
        }
        
        autoTable(doc, {
            startY: yPos,
            head: [['#', 'Product Description', 'HSN', 'Qty', 'Unit Price', 'Disc %', 'Tax %', 'Amount']],
            body: tableBody,
            theme: 'grid',
            headStyles: { 
                fillColor: [66, 103, 178],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 9,
                halign: 'center'
            },
            bodyStyles: {
                fontSize: 8,
                lineColor: [220, 220, 220],
                lineWidth: 0.1
            },
            alternateRowStyles: {
                fillColor: [250, 250, 250]
            },
            columnStyles: {
                0: { cellWidth: 8, halign: 'center' },
                1: { cellWidth: 55, halign: 'left' },
                2: { cellWidth: 15, halign: 'center' },
                3: { cellWidth: 10, halign: 'center' },
                4: { cellWidth: 20, halign: 'right' },
                5: { cellWidth: 12, halign: 'center' },
                6: { cellWidth: 12, halign: 'center' },
                7: { cellWidth: 25, halign: 'right' }
            },
            margin: { left: margin, right: margin },
            tableWidth: 'auto'
        });
        
        // =====================================================
        // SUMMARY SECTION - USING ACTUAL VALUES
        // =====================================================
        const finalY = doc.lastAutoTable.finalY + 10;
        
        // Position for summary - right aligned
        const summaryX = pageWidth - margin - 65;
        let summaryY = finalY;
        
        // Helper function to add clean summary line
        const addCleanSummaryLine = (label, value, isBold = false) => {
            if (isBold) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(66, 103, 178);
            } else {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(80, 80, 80);
            }
            
            doc.text(label, summaryX, summaryY);
            doc.text(value, summaryX + 55, summaryY, { align: 'right' });
            summaryY += isBold ? 7 : 5;
        };
        
        // Add ALL summary lines with actual values
        addCleanSummaryLine('Subtotal:', `₹ ${subtotal.toFixed(2)}`);
        
        // Calculate CGST and SGST (assuming 50/50 split of total tax)
        const cgst = tax / 2;
        const sgst = tax / 2;
        
        addCleanSummaryLine('CGST:', `₹ ${cgst.toFixed(2)}`);
        addCleanSummaryLine('SGST:', `₹ ${sgst.toFixed(2)}`);
        addCleanSummaryLine('Additional Discount:', `- ₹ ${discount.toFixed(2)}`);
        addCleanSummaryLine('Shipping:', `₹ ${shipping.toFixed(2)}`);
        addCleanSummaryLine('Adjustment:', `₹ ${adjustment.toFixed(2)}`);
        addCleanSummaryLine('Round Off:', `₹ ${roundOff.toFixed(2)}`);
        
        summaryY += 2;
        
        // Simple separator line
        doc.setDrawColor(200, 200, 200);
        doc.line(summaryX - 2, summaryY - 2, summaryX + 60, summaryY - 2);
        
        summaryY += 2;
        
        // Grand Total
        addCleanSummaryLine('Grand Total:', `₹ ${grandTotal.toFixed(2)}`, true);
        
        // =====================================================
        // NOTES AND TERMS
        // =====================================================
        let notesY = finalY + 90;
        
        // Notes section
        const notes = fullQuotation.notes || quotation.notes;
        if (notes) {
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, notesY - 5, pageWidth - margin, notesY - 5);
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(40, 40, 40);
            doc.text('Notes:', margin, notesY);
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(80, 80, 80);
            const notesLines = doc.splitTextToSize(notes, 170);
            doc.text(notesLines, margin, notesY + 5);
            
            notesY += (notesLines.length * 5) + 10;
        }
        
        // Terms & Conditions
        const terms = fullQuotation.terms_conditions || quotation.terms_conditions;
        if (terms) {
            if (!notes) {
                doc.setDrawColor(200, 200, 200);
                doc.line(margin, notesY - 5, pageWidth - margin, notesY - 5);
            }
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(40, 40, 40);
            doc.text('Terms & Conditions:', margin, notesY);
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const termsLines = doc.splitTextToSize(terms, 170);
            doc.text(termsLines, margin, notesY + 5);
        }
        
        // =====================================================
        // FOOTER
        // =====================================================
        
        // Bottom decorative line
        doc.setDrawColor(66, 103, 178);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        
        // Footer text
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        
        // Left side - Company info
        doc.text('This is a computer generated quotation - valid with digital signature', margin, pageHeight - 8);
        
        // Right side - Page number
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(7);
            doc.setTextColor(150, 150, 150);
            doc.text(
                `Page ${i} of ${pageCount} | Generated on ${dayjs().format('DD MMM YYYY, HH:mm')}`,
                pageWidth - margin - 50,
                pageHeight - 8,
                { align: 'right' }
            );
        }
        
        // Close loading Swal
        MySwal.close();
        
        // Save PDF
        const fileName = `Quotation_${quotationNo.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        doc.save(fileName);
        
        // Success message
        MySwal.fire({
            icon: 'success',
            title: 'Success',
            text: 'PDF downloaded successfully',
            timer: 1500,
            showConfirmButton: false
        });
    } catch (error) {
        console.error('Error generating PDF:', error);
        MySwal.close();
        MySwal.fire({
            icon: 'error',
            title: 'PDF Generation Failed',
            text: error.message || 'Unknown error occurred',
            confirmButtonText: 'OK'
        });
    } finally {
        setLoading(false);
    }
};
    // Handle select all rows
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(quotations.map(q => q.id));
        } else {
            setSelectedRows([]);
        }
    };

    // Handle select row
    const handleSelectRow = (id, checked) => {
        if (checked) {
            setSelectedRows([...selectedRows, id]);
        } else {
            setSelectedRows(selectedRows.filter(rowId => rowId !== id));
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const badges = {
            'draft': { class: 'badge bg-secondary', icon: <FileText size={12} /> },
            'pending': { class: 'badge bg-warning', icon: <Clock size={12} /> },
            'approved': { class: 'badge bg-success', icon: <CheckCircle size={12} /> },
            'rejected': { class: 'badge bg-danger', icon: <XCircle size={12} /> },
            'expired': { class: 'badge bg-dark', icon: <AlertTriangle size={12} /> },
            'converted': { class: 'badge bg-info', icon: <RefreshCw size={12} /> }
        };
        const badge = badges[status] || { class: 'badge bg-secondary', icon: null };
        return (
            <span className={badge.class}>
                {badge.icon} {status}
            </span>
        );
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Format date
    const formatDate = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    };

    // Check if quotation is expired
    const isExpired = (expiryDate) => {
        return expiryDate && dayjs(expiryDate).isBefore(dayjs());
    };

    // Calculate statistics
    const getSummaryStats = () => {
        const stats = {
            totalQuotations: pagination.total,
            totalAmount: 0,
            pendingCount: 0,
            approvedCount: 0,
            convertedCount: 0
        };

        quotations.forEach(q => {
            stats.totalAmount += q.grand_total || 0;
            if (q.status === 'pending') stats.pendingCount++;
            if (q.status === 'approved') stats.approvedCount++;
            if (q.status === 'converted') stats.convertedCount++;
        });

        return stats;
    };

    const stats = getSummaryStats();

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    // Table columns with PDF download button
    const columns = [
        {
            title: <input
                type="checkbox"
                className="form-check-input"
                checked={selectedRows.length === quotations.length && quotations.length > 0}
                onChange={handleSelectAll}
            />,
            dataIndex: "checkbox",
            key: "checkbox",
            width: "40px",
            render: (text, record) => (
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedRows.includes(record.id)}
                    onChange={(e) => handleSelectRow(record.id, e.target.checked)}
                />
            ),
        },
        {
            title: "Quotation No",
            dataIndex: "quotation_no",
            sorter: true,
            render: (text, record) => (
                <div>
                    <strong>{text}</strong>
                    <br />
                    <small className="text-muted">Created: {formatDate(record.created_at)}</small>
                </div>
            ),
        },
        {
            title: "Date",
            dataIndex: "quotation_date",
            sorter: true,
            render: (text, record) => (
                <div>
                    <div>{formatDate(text)}</div>
                    {record.expiry_date && (
                        <small className={isExpired(record.expiry_date) ? 'text-danger' : 'text-muted'}>
                            Expires: {formatDate(record.expiry_date)}
                        </small>
                    )}
                </div>
            ),
        },
        {
            title: "Customer",
            dataIndex: "customer_name",
            sorter: true,
            render: (text, record) => (
                <div className="d-flex align-items-center">
                    <div className="avatar avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center text-white me-2">
                        {text?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div>{text}</div>
                        <small className="text-muted">{record.customer_phone || ''}</small>
                    </div>
                </div>
            ),
        },
        {
            title: "Amount",
            dataIndex: "grand_total",
            sorter: true,
            render: (text, record) => (
                <div>
                    <strong className="text-primary">{formatCurrency(text)}</strong>
                    <br />
                    <small className="text-muted">Items: {record.items_count || 0}</small>
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            sorter: true,
            render: (text) => getStatusBadge(text),
        },
        {
            title: "Valid Till",
            dataIndex: "expiry_date",
            render: (text) => (
                <span className={isExpired(text) ? 'text-danger' : ''}>
                    {formatDate(text)}
                    {isExpired(text) && <small className="d-block text-danger">Expired</small>}
                </span>
            ),
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (text, record) => (
                <div className="btn-group btn-group-sm" role="group">
                    <button
                        className="btn btn-outline-info"
                        onClick={() => handleView(record)}
                        title="View Details"
                    >
                        <Eye size={14} />
                    </button>
                    <button
                        className="btn btn-outline-primary"
                        onClick={() => handleEdit(record)}
                        title="Edit"
                        disabled={record.status === 'converted' || record.status === 'expired'}
                    >
                        <Edit size={14} />
                    </button>
                    <button
                        className="btn btn-outline-success"
                        onClick={() => handleConvertToSale(record.id)}
                        title="Convert to Sale"
                        disabled={record.status === 'converted' || record.status === 'expired'}
                    >
                        <DollarSign size={14} />
                    </button>
                    {/* PDF Download Button */}
                    <button
                        className="btn btn-outline-warning"
                        onClick={() => downloadQuotationPDF(record)}
                        title="Download PDF"
                        disabled={loading}
                    >
                        <File size={14} />
                    </button>
                    <button
                        className="btn btn-outline-danger"
                        onClick={() => handleDelete(record)}
                        title="Delete"
                        disabled={record.status === 'converted'}
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Quotations</h4>
                            <h6>Manage your sales quotations</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        {selectedRows.length > 0 && (
                            <li>
                                <button className="btn btn-danger btn-sm" onClick={() => {}}>
                                    <Trash2 size={16} className="me-1" />
                                    Delete ({selectedRows.length})
                                </button>
                            </li>
                        )}
                        <li>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate('/create-quotation')}
                            >
                                <PlusCircle size={18} className="me-2" />
                                New Quotation
                            </button>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-primary">
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success">
                                    <FileText size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={() => window.print()}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={() => loadQuotations()}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Quotations</h5>
                                <h3 className="mb-0 text-primary">{stats.totalQuotations}</h3>
                                <small>All time</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Amount</h5>
                                <h3 className="mb-0 text-primary">{formatCurrency(stats.totalAmount)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Pending</h5>
                                <h3 className="mb-0 text-warning">{stats.pendingCount}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Approved</h5>
                                <h3 className="mb-0 text-success">{stats.approvedCount}</h3>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="card table-list-card">
                    <div className="card-body">
                        <div className="table-top">
                            <div className="search-set">
                                <div className="search-input">
                                    <input
                                        type="text"
                                        placeholder="Search by quotation no, customer..."
                                        className="form-control form-control-sm formsearch"
                                        value={filters.search}
                                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button className="btn btn-searchset" onClick={handleSearch}>
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="search-path">
                                <div className="d-flex align-items-center">
                                    <button 
                                        className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} 
                                        onClick={toggleFilterVisibility}
                                    >
                                        <Filter size={18} />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="form-sort">
                                <Sliders size={16} className="info-img me-2" />
                                <Select
                                    className="select"
                                    options={sortOptions}
                                    value={sortOptions.find(opt => opt.value === sortOption)}
                                    onChange={handleSortChange}
                                    placeholder="Sort by"
                                    isClearable={false}
                                />
                            </div>
                        </div>

                        {/* Filter Panel */}
                        <div
                            className={`card${isFilterVisible ? ' visible' : ''}`}
                            id="filter_inputs"
                            style={{ display: isFilterVisible ? 'block' : 'none' }}
                        >
                            <div className="card-body pb-0">
                                <div className="row">
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <User size={16} className="info-img" />
                                            <Select
                                                options={customers}
                                                value={customers.find(c => c.value === filters.customer_id)}
                                                onChange={(opt) => handleFilterChange('customer_id', opt?.value || 0)}
                                                placeholder="All Customers"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <StopCircle size={16} className="info-img" />
                                            <Select
                                                options={statusFilterOptions}
                                                value={statusFilterOptions.find(s => s.value === filters.status)}
                                                onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                                placeholder="All Status"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-sm-12 col-12">
                                        <div className="input-blocks">
                                            <div className="position-relative daterange-wraper">
                                                <Calendar size={16} className="info-img" />
                                                <RangePicker
                                                    value={filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : null}
                                                    onChange={handleDateRangeChange}
                                                    format="DD/MM/YYYY"
                                                    placeholder={['From Date', 'To Date']}
                                                    style={{ width: '100%' }}
                                                    ranges={datePresets}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <button className="btn btn-primary w-100" onClick={() => loadQuotations(1)}>
                                                <Search size={16} className="me-2" />
                                                Apply Filters
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <button className="btn btn-secondary w-100" onClick={resetFilters}>
                                                <RotateCcw size={16} className="me-2" />
                                                Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quotations Table */}
                        <div className="table-responsive">
                            <Table 
                                columns={columns} 
                                dataSource={quotations} 
                                loading={loading}
                                pagination={{
                                    current: pagination.page,
                                    pageSize: pagination.limit,
                                    total: pagination.total,
                                    onChange: handlePageChange,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} quotations`
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {modalType === 'view' && selectedQuotation && (
                <ViewQuotation
                    quotation={selectedQuotation}
                    onClose={handleModalClose}
                />
            )}

            {modalType === 'edit' && selectedQuotation && (
                <EditQuotation
                    quotationId={selectedQuotation.id}
                    onSuccess={handleSuccess}
                    onClose={handleModalClose}
                    isOpen={true}
                />
            )}
        </div>
    );
};

export default QuotationList;